import { eventsTicketCreate } from "@scayla/api-client-fetch";
import { baseConfig } from "@scayla/core";

type EventStreamHandler = (data: unknown) => void;
type EventStreamErrorHandler = () => void;

const RECONNECT_DELAY_MS = 3000;

interface EventStreamMessage {
  code: string;
  data: unknown;
}

interface EventStreamConnection {
  on: (code: string, handler: EventStreamHandler) => () => void;
  onError: (handler: EventStreamErrorHandler) => () => void;
  opened: Promise<void>;
  close: () => void;
}

/**
 * Opens one SSE connection to GET /events/stream. EventSource can't send an Authorization header,
 * so a short-lived ticket (POST /events/ticket, normal bearer auth) is fetched first and passed as
 * a query param instead.
 *
 * Native EventSource retry can't refresh an expired ticket, so onerror closes and reconnects
 * manually (fresh ticket fetch after RECONNECT_DELAY_MS, retried indefinitely) rather than relying
 * on the browser's built-in retry.
 */
function createEventStreamConnection(): EventStreamConnection {
  const handlers = new Map<string, Set<EventStreamHandler>>();
  const errorHandlers = new Set<EventStreamErrorHandler>();
  let eventSource: EventSource | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  let closed = false;
  let markOpened!: () => void;

  /**
   * Resolves the first time the connection is actually established. An event is published once and
   * never replayed, so a caller that triggers the work producing its event must await this first —
   * a ticket fetch plus handshake is long enough for a fast job to finish inside it. Never rejects:
   * a failed attempt is retried, so guard the await with a timeout of your own.
   */
  const opened = new Promise<void>((resolve) => {
    markOpened = resolve;
  });

  function dispatch(event: MessageEvent<string>) {
    let message: EventStreamMessage;
    try {
      message = JSON.parse(event.data) as EventStreamMessage;
    } catch (err) {
      console.error("[useEventStream] failed to parse event payload:", err);
      return;
    }

    handlers.get(message.code)?.forEach((handler) => handler(message.data));
  }

  function scheduleReconnect() {
    if (closed || reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = undefined;
      void connect();
    }, RECONNECT_DELAY_MS);
  }

  async function connect() {
    if (closed) return;

    let ticketResult: Awaited<ReturnType<typeof eventsTicketCreate<false>>>;
    try {
      ticketResult = await eventsTicketCreate();
    } catch {
      // An unreachable server (e.g. mid-restart) rejects the fetch instead of returning `error`.
      scheduleReconnect();
      return;
    }

    if (closed) return;
    const { data, error } = ticketResult;
    if (error || !data) {
      scheduleReconnect();
      return;
    }

    const es = new EventSource(`${baseConfig.scaylaApiUrl}/events/stream?ticket=${encodeURIComponent(data.ticket)}`);
    es.addEventListener("notice", dispatch);
    es.addEventListener("action", dispatch);
    es.onopen = () => markOpened();
    es.onerror = () => {
      es.close();
      if (eventSource === es) {
        eventSource = null;
      }
      errorHandlers.forEach((handler) => handler());
      scheduleReconnect();
    };

    eventSource = es;
  }

  function on(code: string, handler: EventStreamHandler) {
    let codeHandlers = handlers.get(code);
    if (!codeHandlers) {
      codeHandlers = new Set();
      handlers.set(code, codeHandlers);
    }
    codeHandlers.add(handler);

    return () => codeHandlers?.delete(handler);
  }

  /**
   * Notified on every dropped connection, before the reconnect is scheduled. Whatever the backend
   * published during the gap is lost, so a caller waiting on a one-shot event has to treat this as
   * an unknown outcome rather than keep waiting.
   */
  function onError(handler: EventStreamErrorHandler) {
    errorHandlers.add(handler);

    return () => errorHandlers.delete(handler);
  }

  function close() {
    closed = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    }
    eventSource?.close();
    eventSource = null;
  }

  void connect();

  return { on, onError, opened, close };
}

let shared: EventStreamConnection | undefined;
let refCount = 0;

/**
 * Every caller shares one page-scoped connection — opening a separate EventSource per caller can
 * exhaust the browser's per-origin connection limit alongside the other long-lived streams already
 * open elsewhere in the app (file classification, upload progress), which is what caused drag-drop's
 * own connection to never open in time. close() only releases this caller's share; the underlying
 * connection closes once the last caller releases it.
 */
export default function useEventStream(): EventStreamConnection {
  shared ??= createEventStreamConnection();
  const connection = shared;
  refCount++;

  let released = false;
  function close() {
    if (released) return;
    released = true;
    refCount--;
    if (refCount <= 0) {
      connection.close();
      if (shared === connection) shared = undefined;
      refCount = 0;
    }
  }

  return { on: connection.on, onError: connection.onError, opened: connection.opened, close };
}

/** How long a caller that starts a job waits for the stream before starting it anyway. */
export const EVENT_STREAM_OPEN_TIMEOUT_MS = 3000;

/**
 * Waits for `stream` to open, at most `timeoutMs`. `opened` never rejects, so this is the bounded
 * wait to use before starting a job whose one-time events the caller must not miss.
 *
 * @returns `true` once the stream is open, `false` when the timeout ran out first.
 */
export function awaitEventStreamOpen(
  stream: { opened: Promise<void> },
  timeoutMs = EVENT_STREAM_OPEN_TIMEOUT_MS,
): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), timeoutMs);
  });
  const opened = stream.opened.then(() => true);
  return Promise.race([opened, timedOut]).finally(() => clearTimeout(timer));
}

/** Test-only: clears the shared connection so each test starts from a fresh one. */
export function resetEventStreamForTests() {
  shared = undefined;
  refCount = 0;
}
